/**
 * マップ選択画面
 * ゲーム開始前にプレイするマップを選択する
 */
import { useState, useEffect } from 'react';
import { getAvailableMaps, GAME_REGISTRY } from '../config/gameRegistry';
import { useI18n, type Language } from '../i18n';
import { BASE_PATH } from '../config';
import { loadPendingScore, clearPendingScore } from '../utils/storage';
import { submitScore } from '../utils/scoreApi';

interface MapSelectionScreenProps {
  /** WIPマップを表示するか */
  showWIP?: boolean;
}

/**
 * マップ選択画面コンポーネント
 */
export function MapSelectionScreen({ showWIP = true }: MapSelectionScreenProps) {
  const { language, setLanguage, t } = useI18n();
  const maps = getAvailableMaps(showWIP);
  const [pendingResult, setPendingResult] = useState<{ success: boolean; rank?: number; mapType?: string } | null>(null);

  // ペンディングスコアの自動リトライ
  useEffect(() => {
    const pending = loadPendingScore();
    if (!pending) return;
    submitScore(pending)
      .then((result) => {
        if (result.success) {
          clearPendingScore();
          setPendingResult({ success: true, rank: result.rank, mapType: pending.mapType });
        }
        // 失敗時はlocalStorageに残したまま（次回再試行）
      })
      .catch(() => {
        // サーバー未起動 — 次回再試行
      });
  }, []);

  const handleLanguageChange = (newLang: Language) => {
    setLanguage(newLang);
  };

  const handleMapSelect = (mapId: string) => {
    // 全マップ共通: 直接遷移
    window.location.href = `${BASE_PATH}${mapId}/`;
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      backgroundColor: 'hsl(var(--background))',
      color: 'hsl(var(--foreground))',
      padding: '24px 20px',
      boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* ヘッダー（固定） */}
      <div style={{
        textAlign: 'center',
        marginBottom: '12px',
        flexShrink: 0,
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          marginBottom: '-4px',
          color: 'hsl(var(--foreground))',
        }}>
          {t.mapSelection.title}
        </h1>
        <p style={{
          fontSize: '13px',
          color: 'hsl(var(--muted-foreground))',
          marginBottom: '6px',
          letterSpacing: '0.5px',
        }}>
          Unofficial Fan-Made Tool
        </p>

        {/* 言語選択 */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}>
          <span style={{
            fontSize: '12px',
            color: 'hsl(var(--muted-foreground))',
          }}>
            表示言語選択 / Language:
          </span>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button
              onClick={() => handleLanguageChange('en')}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                backgroundColor: language === 'en'
                  ? 'hsl(var(--primary))'
                  : 'hsl(var(--muted))',
                color: language === 'en'
                  ? 'hsl(var(--primary-foreground))'
                  : 'hsl(var(--muted-foreground))',
              }}
            >
              English
            </button>
            <button
              onClick={() => handleLanguageChange('ja')}
              style={{
                padding: '4px 8px',
                fontSize: '12px',
                borderRadius: '4px',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                backgroundColor: language === 'ja'
                  ? 'hsl(var(--primary))'
                  : 'hsl(var(--muted))',
                color: language === 'ja'
                  ? 'hsl(var(--primary-foreground))'
                  : 'hsl(var(--muted-foreground))',
              }}
            >
              日本語
            </button>
          </div>
        </div>
      </div>

      {/* グローバルメニュー */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '12px',
      }}>
        <button
          onClick={() => { window.location.href = `${BASE_PATH}hall-of-honor`; }}
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '6px',
            border: '1px solid hsl(var(--border))',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          {t.mapSelection.hallOfHonor}
        </button>
        <button
          onClick={() => { window.location.href = `${BASE_PATH}achievements`; }}
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '6px',
            border: '1px solid hsl(var(--border))',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          {t.mapSelection.achievements}
        </button>
        <button
          onClick={() => { window.location.href = `${BASE_PATH}statistics`; }}
          style={{
            padding: '8px 16px',
            fontSize: '13px',
            fontWeight: 600,
            borderRadius: '6px',
            border: '1px solid hsl(var(--border))',
            backgroundColor: 'hsl(var(--card))',
            color: 'hsl(var(--card-foreground))',
            cursor: 'pointer',
            transition: 'all 0.15s ease',
          }}
        >
          {t.mapSelection.statistics}
        </button>
      </div>

      {/* ペンディングスコア復旧通知 */}
      {pendingResult?.success && (
        <div style={{
          backgroundColor: 'hsl(var(--success) / 0.15)',
          color: 'hsl(var(--success))',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: 500,
          marginBottom: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span>Score registered! Rank: {pendingResult.rank} ({pendingResult.mapType})</span>
          <button
            onClick={() => setPendingResult(null)}
            style={{
              all: 'unset',
              cursor: 'pointer',
              fontSize: '16px',
              lineHeight: 1,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* デバイス推奨メッセージ */}
      <p style={{
        fontSize: '12px',
        color: 'hsl(var(--muted-foreground))',
        marginBottom: '12px',
        textAlign: 'center',
      }}>
        {t.mapSelection.deviceRecommendation}
      </p>

      {/* マップカード一覧（スクロール可能） */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        width: '100%',
        maxWidth: '900px',
        padding: '4px',
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
        }}>
        {maps.map((map) => {
          const entry = GAME_REGISTRY[map.id];
          const isWIP = entry?.isWIP;

          return (
            <div
              key={map.id}
              style={{
                padding: '16px',
                borderRadius: '10px',
                border: '1px solid hsl(var(--border))',
                backgroundColor: 'hsl(var(--card))',
                color: 'hsl(var(--card-foreground))',
                textAlign: 'left',
                opacity: isWIP ? 0.5 : 1,
                transition: 'all 0.2s ease',
                position: 'relative',
              }}
            >
              {/* WIPバッジ */}
              {isWIP && (
                <span style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  fontSize: '10px',
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: '4px',
                  backgroundColor: 'hsl(var(--muted))',
                  color: 'hsl(var(--muted-foreground))',
                }}>
                  {t.mapSelection.comingSoon}
                </span>
              )}

              {/* マップヘッダー（クリック可能） */}
              <button
                onClick={() => handleMapSelect(map.id)}
                disabled={isWIP}
                style={{
                  all: 'unset',
                  display: 'block',
                  width: '100%',
                  cursor: isWIP ? 'not-allowed' : 'pointer',
                }}
              >
                {/* マップ名 */}
                <h2 style={{
                  fontSize: '17px',
                  fontWeight: 600,
                  marginBottom: '6px',
                }}>
                  {t.mapSelection.maps[map.i18nMapKey].name}
                </h2>

                {/* デザイナー */}
                <p style={{
                  fontSize: '12px',
                  color: 'hsl(var(--muted-foreground))',
                  marginBottom: '2px',
                }}>
                  {t.gameStartCommon.designedBy} {entry?.designer}
                </p>

                {/* 難易度 */}
                <p style={{
                  fontSize: '12px',
                  color: 'hsl(var(--muted-foreground))',
                  marginBottom: '2px',
                }}>
                  {t.mapSelection.difficulty} {t.mapSelection.difficultyStars[map.difficulty]}
                </p>

                {/* プレイ人数 */}
                <p style={{
                  fontSize: '12px',
                  color: 'hsl(var(--muted-foreground))',
                  marginBottom: '8px',
                }}>
                  {t.mapSelection.players} {entry?.numPlayers === 1 ? t.mapSelection.solo : `${entry?.numPlayers}`}
                </p>

                {/* 説明 */}
                <p style={{
                  fontSize: '12px',
                  color: 'hsl(var(--muted-foreground))',
                  lineHeight: 1.5,
                }}>
                  {t.mapSelection.maps[map.i18nMapKey].description}
                </p>
              </button>
            </div>
          );
        })}
        </div>
      </div>

      {/* フッター（固定） */}
      <div style={{
        marginTop: '16px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        fontSize: '12px',
        flexShrink: 0,
      }}>
        <a
          href={`${BASE_PATH}editor/`}
          style={{
            color: 'hsl(var(--muted-foreground))',
            textDecoration: 'none',
            opacity: 0.7,
          }}
        >
          {t.mapSelection.mapEditor}
        </a>
        <div style={{
          color: 'hsl(var(--muted-foreground))',
          opacity: 0.5,
          fontSize: '11px',
          textAlign: 'left',
          maxWidth: '900px',
          margin: 0,
        }}>
          <p style={{ margin: 0, textAlign: 'center', lineHeight: 1.6 }}>
            This is an unofficial fan project for personal use by owners of the physical board game, UI and implementation by{' '}
            <a
              href="https://x.com/luxurytax150"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'inherit',
                textDecoration: 'underline',
              }}
            >
              @luxurytax150
            </a>
            .<br />All rights to "Age of Steam" and related game designs belong to their respective creators. We will promptly remove any content upon request by the rights holders.
          </p>
        </div>
      </div>
    </div>
  );
}
